import { createPortal } from 'react-dom';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, UserPlus, Users, X } from 'lucide-react';

import {
	ButtonGroup,
	DialogIcon,
	DialogTitle,
	PunchupDialogue,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { postToast } from 'features/common/utils/postFlash';
import { airbrake } from 'ErrorBoundary';
import { isAdminRole } from 'utils/utils';

import { assignScanner, getPartnerMembers, removeScanner } from '../services';
import { gatesKey, PARTNER_MEMBERS_KEY, partnerMemberName } from '../hooks/useGatesManagement';
import { Gate } from '../types';
import { AddScannerDialog } from './AddScannerDialog';
import styles from '../PunchupGatesManagement.module.css';

interface ManageScannersDialogProps {
	gate: Gate;
	eventId: string;
	onClose: () => void;
}

/** Per-gate scanner assignment: lists partner members with an assign/remove toggle, plus an
 * admin-only "Invite scanner" entry. Assigned scanner_ids match members[].user_id. */
export const ManageScannersDialog = ({ gate, eventId, onClose }: ManageScannersDialogProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [isInviting, setIsInviting] = useState(false);

	// Read the token once (a render-time localStorage read isn't reactive); the backend enforces admin-only too.
	const [isAdmin] = useState(() => isAdminRole(localStorage.getItem('accessToken') || ''));
	const { data: partnerData } = useQuery({ queryKey: PARTNER_MEMBERS_KEY, queryFn: getPartnerMembers });
	const invalidateGates = () => queryClient.invalidateQueries({ queryKey: gatesKey(eventId) });

	const assign = useMutation({
		mutationFn: (scannerId: number) => assignScanner(eventId, gate.ID, scannerId),
		onSuccess: () => {
			invalidateGates();
			postToast('success', t('gates-assign-success', 'Scanner assigned'));
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error assigning scanner', context: { error } });
			postToast('error', t('gates-assign-error', 'Could not assign the scanner'));
		},
	});

	const remove = useMutation({
		mutationFn: (scannerId: number) => removeScanner(eventId, gate.ID, scannerId),
		onSuccess: () => {
			invalidateGates();
			postToast('success', t('gates-remove-success', 'Scanner removed'));
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error removing scanner', context: { error } });
			postToast('error', t('gates-remove-error', 'Could not remove the scanner'));
		},
	});

	const assignedIds = gate.assigned_scanners.map((scanner) => scanner.scanner_id);

	// Busy is derived per-row from each mutation's own in-flight variable, so two quick clicks on
	// different members can't clobber a single shared "loading id" (the row whose request is in flight
	// is the only one that shows the spinner).
	const isRowBusy = (userId: number) =>
		(assign.isLoading && assign.variables === userId) || (remove.isLoading && remove.variables === userId);

	const toggle = (userId: number, assigned: boolean) => {
		if (assigned) remove.mutate(userId);
		else assign.mutate(userId);
	};

	return createPortal(
		<>
			<PunchupDialogue maxWidth={560} onClose={onClose}>
				<DialogIcon>
					<Users size={24} />
				</DialogIcon>
				<DialogTitle>{t('gates-scanners-title', 'Scanners — {{gate}}', { gate: gate.name })}</DialogTitle>

				<div className={styles.memberList}>
					{partnerData?.members?.length ? (
						partnerData.members.map((member) => {
							const assigned = assignedIds.includes(member.user_id);
							const busy = isRowBusy(member.user_id);
							return (
								<div className={styles.memberRow} key={member.id}>
									<div className={styles.memberInfo}>
										<span className={styles.memberName}>{partnerMemberName(member)}</span>
										{member.email && <span className={styles.memberEmail}>{member.email}</span>}
									</div>
									<PunchupButton
										type="button"
										variant={assigned ? 'secondary' : 'outline'}
										onClick={() => toggle(member.user_id, assigned)}
										disabled={busy}
										isLoading={busy}
										icon={assigned ? <X size={16} /> : <Check size={16} />}
										text={assigned ? t('gates-remove', 'Remove') : t('gates-assign', 'Assign')}
									/>
								</div>
							);
						})
					) : (
						<p className={styles.emptyMembers}>{t('gates-no-members', 'No team members to assign yet.')}</p>
					)}
				</div>

				<ButtonGroup>
					{isAdmin && (
						<PunchupButton
							type="button"
							variant="outline"
							icon={<UserPlus size={16} />}
							text={t('gates-invite-cta', 'Invite scanner')}
							onClick={() => setIsInviting(true)}
						/>
					)}
					<PunchupButton type="button" variant="secondary" text={t('button-close', 'Close')} onClick={onClose} />
				</ButtonGroup>
			</PunchupDialogue>

			{isInviting && <AddScannerDialog onClose={() => setIsInviting(false)} />}
		</>,
		document.body
	);
};
